import { useMutation } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, ApiError, fieldIssues } from '../api';
import { trapTab } from '../ui/focus';
import { DialogLayer } from '../ui/Dialog';
import { useUnsavedGuard } from '../ui/unsaved';

/**
 * "Zapisz jako szablon" (the whole packlist, the event's scopes and "whose system", an optional checklist) or
 * "Zapisz jako blok" (the chosen lines). `onSaved` gets the new name.
 */
export function SaveAsDialog({ eventId, mode, lineIds, onClose, onSaved }: { eventId: string; mode: 'template' | 'block'; lineIds: string[]; onClose: () => void; onSaved: (name: string) => void }) {
  const [name, setName] = useState('');
  const [checklist, setChecklist] = useState('');
  const save = useMutation({
    mutationFn: () =>
      mode === 'template'
        ? api<{ id: string }>(`/api/packlists/${eventId}/save-as-template`, {
            method: 'POST',
            body: { name: name.trim(), checklist: checklist.split('\n').map((t) => t.trim()).filter((t) => t !== '') },
          })
        : api<{ id: string }>(`/api/packlists/${eventId}/save-as-block`, { method: 'POST', body: { name: name.trim(), lineIds } }),
    onSuccess: () => onSaved(name.trim()),
  });
  const error = save.error instanceof ApiError ? save.error : null;
  const title = mode === 'template' ? 'Zapisz jako szablon' : `Zapisz jako blok (${lineIds.length})`;

  function submit(e: FormEvent) {
    e.preventDefault();
    if (name.trim() !== '') save.mutate();
  }
  // owner 2026-10-23 21:14: typed and not saved: leaving asks first
  const guardRef = useUnsavedGuard({
    dirty: name.trim() !== '' || checklist.trim() !== '',
    save: async () => {
      if (name.trim() === '') return false;
      await save.mutateAsync();
      return true;
    },
  });

  return (
    <div className="dialog-backdrop">
      <form
        ref={guardRef}
        className="dialog panel form"
        role="dialog"
        aria-modal="true"
        aria-labelledby="save-as-title"
        data-feature-id="packlists.save_as"
        onSubmit={submit}
        onKeyDown={(e) => {
          if (e.key === 'Tab') trapTab(e.currentTarget, e.nativeEvent);
        }}
      >
        <DialogLayer onClose={onClose} busy={save.isPending} />
        <h2 id="save-as-title">{title}</h2>
        <p className="muted">
          {mode === 'template'
            ? 'Szablon zabierze wszystkie pozycje z ilościami (powtórzenia i różne terminy zsumowane), zakres prac i system wydarzenia.'
            : 'Blok zabierze zaznaczone pozycje z ilościami; powtórzenia tego samego sprzętu się zsumują.'}
        </p>
        {error && (
          <p className="alert" role="alert">
            {error.message}
            {fieldIssues(error).map((i) => (
              <span key={i.path + i.message}> {i.message}</span>
            ))}
          </p>
        )}
        <label className="field">
          <span>Nazwa {mode === 'template' ? 'szablonu' : 'bloku'}</span>
          <input autoFocus required value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" placeholder={mode === 'template' ? 'np. Koncert plenerowy standard' : 'np. Mikrofony'} />
        </label>
        {mode === 'template' && (
          <label className="field">
            <span>Lista kontrolna (jeden punkt w wierszu, można później)</span>
            <textarea rows={3} value={checklist} onChange={(e) => setChecklist(e.target.value)} />
          </label>
        )}
        <div className="actions">
          <button className="btn primary" type="submit" disabled={save.isPending || name.trim() === ''}>
            {save.isPending ? 'Zapisywanie…' : 'Zapisz'}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Anuluj
          </button>
        </div>
      </form>
    </div>
  );
}
